import type { Language } from "../../../types/i18n";

// Part of the healthMore module (see ../healthMore.ts). English is the
// reference; every other language must carry the same keys.
// Prefixes: thd (TravelHealthData), famHist (FamilyHistory),
// immuRec (ImmunizationRecords), wellb (WellbeingAssessment).
const en = {
  // TravelHealthData
  thdTitle: "Travel Health Information",
  thdInsurance: "Travel Insurance",
  thdInsuranceProvider: "Insurance provider",
  thdInsuranceProviderPlaceholder: "e.g. Allianz Travel, Travel Guard",
  thdPolicyNumber: "Policy number",
  thdValidUntil: "Valid until",
  thdEmergencyPhone: "Emergency phone number",
  thdEmergencyPhonePlaceholder: "+1 (555) 123-4567",
  thdCoverage: "Coverage",
  thdCovEmergency: "Emergency treatment",
  thdCovHospital: "Hospital stay",
  thdCovMedication: "Medication",
  thdCovRepatriation: "Medical repatriation",
  thdCovDental: "Dental treatment",
  thdCovEye: "Eye care",
  thdCovPhysio: "Physical therapy",
  thdCovPsychotherapy: "Psychotherapy",
  thdCovChronic: "Chronic conditions",
  thdCovPregnancy: "Pregnancy",
  thdCovAccident: "Accident insurance",
  thdHospitalsEdit: "Preferred Hospitals/Clinics",
  thdHospitals: "Preferred Hospitals",
  thdHospitalName: "Clinic name",
  thdAddress: "Address",
  thdPhone: "Phone number",
  thdCountry: "Country",
  thdAddHospital: "Add hospital",
  thdDietaryEdit: "Special Dietary Requirements",
  thdDietary: "Dietary Requirements",
  thdDietVegetarian: "Vegetarian",
  thdDietVegan: "Vegan",
  thdDietHalal: "Halal",
  thdDietKosher: "Kosher",
  thdDietGlutenFree: "Gluten-free",
  thdDietLactoseFree: "Lactose-free",
  thdDietDiabetic: "Diabetic diet",
  thdDietOther: "Other",
  thdSevPreference: "Preference",
  thdSevMild: "Mild",
  thdSevModerate: "Moderate",
  thdSevSevere: "Severe",
  thdSevLifeThreatening: "Life-threatening",
  thdDescriptionOptional: "Description (optional)",
  thdAddDietary: "Add dietary requirement",
  thdMobility: "Mobility Assistance",
  thdWheelchairRequired: "Wheelchair required",
  thdWalkingAid: "Walking aid",
  thdAidNone: "No walking aid",
  thdAidCane: "Cane",
  thdAidWalker: "Walker",
  thdAidCrutches: "Crutches",
  thdAssistanceLevel: "Assistance level",
  thdAssistIndependent: "Independent",
  thdAssistMinimal: "Minimal assistance",
  thdAssistModerate: "Moderate assistance",
  thdAssistFull: "Full assistance",
  thdSpecialRequests: "Special requests",
  thdSpecialRequestsPlaceholder: "Special requirements or aids",
  thdCommunication: "Communication Needs",
  thdPrimaryLanguage: "Primary language",
  thdInterpreterRequired: "Interpreter required",
  thdSignLanguage: "Sign language",
  thdHearingImpairment: "Hearing impairment",
  thdVisualImpairment: "Visual impairment",
  thdCommunicationAids: "Aids",
  thdExemptions: "Vaccine Exemptions",
  thdVaccine: "Vaccine",
  thdExMedical: "Medical",
  thdExReligious: "Religious",
  thdExPhilosophical: "Philosophical",
  thdAddExemption: "Add vaccine exemption",
  thdReason: "Reason",
  thdReasonMedical: "Medical contraindication",
  thdReasonReligious: "Religious reasons",
  thdReasonPhilosophical: "Philosophical reasons",
  thdReasonOther: "Other reasons",
  thdProvider: "Provider",
  thdPolicy: "Policy",
  thdCoveredServices: "Covered services",
  thdEmpty: "No travel health information on file",

  // FamilyHistory
  famHistTitle: "Family History",
  famHistInfoTitle: "Why your family history matters",
  famHistInfoText:
    "This information helps doctors assess genetic risks and recommend preventive measures.",
  famHistRelMother: "Mother",
  famHistRelFather: "Father",
  famHistRelSibling: "Sibling",
  famHistRelChild: "Child",
  famHistRelGrandparent: "Grandparents",
  famHistRelAunt: "Aunt",
  famHistRelUncle: "Uncle",
  famHistRelCousin: "Cousin",
  famHistRelOther: "Other",
  famHistRelationship: "Relationship",
  famHistRelationshipSpecify: "Specify relationship",
  famHistRelationshipPlaceholder: "e.g. stepfather, half-sister",
  famHistCondition: "Condition",
  famHistConditionSelect: "Select or enter a condition",
  famHistOtherConditionPlaceholder: "Enter another condition",
  famHistCatCardiovascular: "Cardiovascular",
  famHistCatCancer: "Cancer",
  famHistCatNeurological: "Neurological",
  famHistCatMetabolic: "Metabolic",
  famHistCatMental: "Mental health",
  famHistCatOther: "Other",
  famHistCondCoronaryArteryDisease: "Coronary artery disease",
  famHistCondHypertension: "High blood pressure",
  famHistCondHeartFailure: "Heart failure",
  famHistCondBreastCancer: "Breast cancer",
  famHistCondColorectalCancer: "Colorectal cancer",
  famHistCondPancreaticCancer: "Pancreatic cancer",
  famHistCondProstateCancer: "Prostate cancer",
  famHistCondOvarianCancer: "Ovarian cancer",
  famHistCondLungCancer: "Lung cancer",
  famHistCondAlzheimers: "Alzheimer's disease",
  famHistCondParkinsons: "Parkinson's disease",
  famHistCondMultipleSclerosis: "Multiple sclerosis",
  famHistCondEpilepsy: "Epilepsy",
  famHistCondDiabetesType2: "Type 2 diabetes",
  famHistCondDiabetesType1: "Type 1 diabetes",
  famHistCondHypothyroidism: "Hypothyroidism",
  famHistCondHyperthyroidism: "Hyperthyroidism",
  famHistCondDepression: "Depression",
  famHistCondSchizophrenia: "Schizophrenia",
  famHistCondBipolar: "Bipolar disorder",
  famHistCondAnxiety: "Anxiety disorder",
  famHistCondArthritis: "Rheumatism/arthritis",
  famHistCondCrohns: "Crohn's disease",
  famHistCondUlcerativeColitis: "Ulcerative colitis",
  famHistCondAsthma: "Asthma",
  famHistStatusActive: "Active/ongoing",
  famHistStatusResolved: "Cured/resolved",
  famHistStatusDeceased: "Deceased (from this condition)",
  famHistGeneticRisk: "Genetic risk (estimated)",
  famHistRiskLow: "Low",
  famHistRiskModerate: "Moderate",
  famHistRiskHigh: "High",
  famHistRiskLowBadge: "Low risk",
  famHistRiskModerateBadge: "Moderate risk",
  famHistRiskHighBadge: "High risk",
  famHistAgeAtOnset: "Age at onset",
  famHistAgeAtDeath: "Age at death",
  famHistYears: "Years",
  famHistCauseOfDeath: "Cause of death (if different)",
  famHistCauseOfDeathShort: "Cause of death",
  famHistCauseOfDeathPlaceholder: "If not the condition above",
  famHistNotes: "Additional notes",
  famHistNotesPlaceholder: "Further details, complications, etc.",
  famHistRemoveEntry: "Remove entry",
  famHistAddEntry: "Add family history entry",
  famHistEmpty: "No family history recorded",
  famHistEntryOne: "{count} entry",
  famHistEntryMany: "{count} entries",
  famHistOnsetAt: "Diagnosed at age {age}",
  famHistDiedAt: "Died at age {age}",

  // ImmunizationRecords
  immuRecTitle: "Immunizations",
  immuRecVaccine: "Vaccine",
  immuRecDate: "Date of vaccination",
  immuRecDoseNumber: "Dose number",
  immuRecLotNumber: "Lot number (optional)",
  immuRecLotPlaceholder: "e.g. ABC123",
  immuRecRemove: "Remove vaccination",
  immuRecAdd: "Add vaccination",
  immuRecEmpty: "No vaccinations recorded",
  immuRecDose: "Dose {n}",
  immuRecOfSeries: "of {n}",
  immuRecStatusNotDocumented: "Not documented",
  immuRecStatusCurrent: "Up to date",
  immuRecStatusBooster: "Booster recommended",
  immuRecStatusExpired: "Expired",
  immuRecVacHepA: "Hepatitis A",
  immuRecVacHepB: "Hepatitis B",
  immuRecVacPneumococcal: "Pneumococcal",
  immuRecVacMmr: "MMR (measles, mumps, rubella)",
  immuRecVacTdap: "Tdap (tetanus, diphtheria, pertussis)",
  immuRecVacInfluenza: "Influenza",
  immuRecVacCovid: "COVID-19",
  immuRecVacYellowFever: "Yellow fever",
  immuRecVacMeningococcal: "Meningococcal",
  immuRecVacJapEnc: "Japanese encephalitis",
  immuRecVacTyphoid: "Typhoid",
  immuRecVacRabies: "Rabies",
};

export const recordsStrings: Record<Language, typeof en> = {
  en,
  de: {
    thdTitle: "Reisespezifische Gesundheitsdaten",
    thdInsurance: "Reiseversicherung",
    thdInsuranceProvider: "Versicherungsanbieter",
    thdInsuranceProviderPlaceholder: "z. B. Allianz Travel, ADAC",
    thdPolicyNumber: "Versicherungsnummer",
    thdValidUntil: "Gültig bis",
    thdEmergencyPhone: "Notfall-Telefonnummer",
    thdEmergencyPhonePlaceholder: "+49 xxx xxx xxxx",
    thdCoverage: "Versicherungsleistungen",
    thdCovEmergency: "Notfallbehandlung",
    thdCovHospital: "Krankenhausaufenthalt",
    thdCovMedication: "Medikamente",
    thdCovRepatriation: "Rücktransport",
    thdCovDental: "Zahnbehandlung",
    thdCovEye: "Augenbehandlung",
    thdCovPhysio: "Physiotherapie",
    thdCovPsychotherapy: "Psychotherapie",
    thdCovChronic: "Chronische Erkrankungen",
    thdCovPregnancy: "Schwangerschaft",
    thdCovAccident: "Unfallversicherung",
    thdHospitalsEdit: "Bevorzugte Krankenhäuser/Kliniken",
    thdHospitals: "Bevorzugte Krankenhäuser",
    thdHospitalName: "Name der Klinik",
    thdAddress: "Adresse",
    thdPhone: "Telefonnummer",
    thdCountry: "Land",
    thdAddHospital: "Krankenhaus hinzufügen",
    thdDietaryEdit: "Besondere Ernährungsanforderungen",
    thdDietary: "Ernährungsanforderungen",
    thdDietVegetarian: "Vegetarisch",
    thdDietVegan: "Vegan",
    thdDietHalal: "Halal",
    thdDietKosher: "Koscher",
    thdDietGlutenFree: "Glutenfrei",
    thdDietLactoseFree: "Laktosefrei",
    thdDietDiabetic: "Diabetiker-Diät",
    thdDietOther: "Sonstige",
    thdSevPreference: "Präferenz",
    thdSevMild: "Mild",
    thdSevModerate: "Moderat",
    thdSevSevere: "Schwer",
    thdSevLifeThreatening: "Lebensgefährlich",
    thdDescriptionOptional: "Beschreibung (optional)",
    thdAddDietary: "Ernährungsanforderung hinzufügen",
    thdMobility: "Mobilitätshilfen",
    thdWheelchairRequired: "Rollstuhl erforderlich",
    thdWalkingAid: "Gehhilfe",
    thdAidNone: "Keine Hilfsmittel",
    thdAidCane: "Gehstock",
    thdAidWalker: "Rollator",
    thdAidCrutches: "Krücken",
    thdAssistanceLevel: "Assistenzstufe",
    thdAssistIndependent: "Selbstständig",
    thdAssistMinimal: "Minimale Hilfe",
    thdAssistModerate: "Moderate Hilfe",
    thdAssistFull: "Vollständige Hilfe",
    thdSpecialRequests: "Besondere Wünsche",
    thdSpecialRequestsPlaceholder: "Spezielle Anforderungen oder Hilfsmittel",
    thdCommunication: "Kommunikationsbedürfnisse",
    thdPrimaryLanguage: "Hauptsprache",
    thdInterpreterRequired: "Dolmetscher erforderlich",
    thdSignLanguage: "Gebärdensprache",
    thdHearingImpairment: "Hörbeeinträchtigung",
    thdVisualImpairment: "Sehbeeinträchtigung",
    thdCommunicationAids: "Hilfsmittel",
    thdExemptions: "Impfbefreiungen",
    thdVaccine: "Impfstoff",
    thdExMedical: "Medizinisch",
    thdExReligious: "Religiös",
    thdExPhilosophical: "Weltanschaulich",
    thdAddExemption: "Impfbefreiung hinzufügen",
    thdReason: "Grund",
    thdReasonMedical: "Medizinische Kontraindikation",
    thdReasonReligious: "Religiöse Gründe",
    thdReasonPhilosophical: "Weltanschauliche Gründe",
    thdReasonOther: "Sonstige Gründe",
    thdProvider: "Anbieter",
    thdPolicy: "Police",
    thdCoveredServices: "Abgedeckte Leistungen",
    thdEmpty: "Keine reisespezifischen Gesundheitsdaten hinterlegt",

    famHistTitle: "Familienanamnese",
    famHistInfoTitle: "Wichtige Information zur Familienanamnese",
    famHistInfoText:
      "Diese Informationen helfen Ärzten, genetische Risiken zu bewerten und präventive Maßnahmen zu empfehlen.",
    famHistRelMother: "Mutter",
    famHistRelFather: "Vater",
    famHistRelSibling: "Geschwister",
    famHistRelChild: "Kind",
    famHistRelGrandparent: "Großeltern",
    famHistRelAunt: "Tante",
    famHistRelUncle: "Onkel",
    famHistRelCousin: "Cousin/Cousine",
    famHistRelOther: "Sonstige",
    famHistRelationship: "Verwandtschaftsgrad",
    famHistRelationshipSpecify: "Verwandtschaftsgrad spezifizieren",
    famHistRelationshipPlaceholder: "z. B. Stiefvater, Halbschwester",
    famHistCondition: "Erkrankung",
    famHistConditionSelect: "Bitte wählen oder eingeben",
    famHistOtherConditionPlaceholder: "Andere Erkrankung eingeben",
    famHistCatCardiovascular: "Herz-Kreislauf",
    famHistCatCancer: "Krebs",
    famHistCatNeurological: "Neurologisch",
    famHistCatMetabolic: "Stoffwechsel",
    famHistCatMental: "Psychisch",
    famHistCatOther: "Sonstige",
    famHistCondCoronaryArteryDisease: "Koronare Herzkrankheit",
    famHistCondHypertension: "Bluthochdruck",
    famHistCondHeartFailure: "Herzinsuffizienz",
    famHistCondBreastCancer: "Brustkrebs",
    famHistCondColorectalCancer: "Darmkrebs",
    famHistCondPancreaticCancer: "Bauchspeicheldrüsenkrebs",
    famHistCondProstateCancer: "Prostatakrebs",
    famHistCondOvarianCancer: "Eierstockkrebs",
    famHistCondLungCancer: "Lungenkrebs",
    famHistCondAlzheimers: "Alzheimer-Demenz",
    famHistCondParkinsons: "Parkinson-Krankheit",
    famHistCondMultipleSclerosis: "Multiple Sklerose",
    famHistCondEpilepsy: "Epilepsie",
    famHistCondDiabetesType2: "Diabetes mellitus Typ 2",
    famHistCondDiabetesType1: "Diabetes mellitus Typ 1",
    famHistCondHypothyroidism: "Schilddrüsenunterfunktion",
    famHistCondHyperthyroidism: "Schilddrüsenüberfunktion",
    famHistCondDepression: "Depression",
    famHistCondSchizophrenia: "Schizophrenie",
    famHistCondBipolar: "Bipolare Störung",
    famHistCondAnxiety: "Angststörung",
    famHistCondArthritis: "Rheuma/Arthritis",
    famHistCondCrohns: "Morbus Crohn",
    famHistCondUlcerativeColitis: "Colitis ulcerosa",
    famHistCondAsthma: "Asthma bronchiale",
    famHistStatusActive: "Aktiv/Laufend",
    famHistStatusResolved: "Geheilt/Überstanden",
    famHistStatusDeceased: "Verstorben (aufgrund dieser Erkrankung)",
    famHistGeneticRisk: "Genetisches Risiko (geschätzt)",
    famHistRiskLow: "Niedrig",
    famHistRiskModerate: "Moderat",
    famHistRiskHigh: "Hoch",
    famHistRiskLowBadge: "Niedriges Risiko",
    famHistRiskModerateBadge: "Moderates Risiko",
    famHistRiskHighBadge: "Hohes Risiko",
    famHistAgeAtOnset: "Alter bei Erkrankung",
    famHistAgeAtDeath: "Alter bei Tod",
    famHistYears: "Jahre",
    famHistCauseOfDeath: "Todesursache (falls abweichend)",
    famHistCauseOfDeathShort: "Todesursache",
    famHistCauseOfDeathPlaceholder: "Falls nicht die oben genannte Erkrankung",
    famHistNotes: "Zusätzliche Notizen",
    famHistNotesPlaceholder: "Weitere Details, Komplikationen usw.",
    famHistRemoveEntry: "Eintrag entfernen",
    famHistAddEntry: "Neuen Familieneintrag hinzufügen",
    famHistEmpty: "Keine Familienanamnese dokumentiert",
    famHistEntryOne: "{count} Eintrag",
    famHistEntryMany: "{count} Einträge",
    famHistOnsetAt: "Erkrankt mit {age} Jahren",
    famHistDiedAt: "Verstorben mit {age} Jahren",

    immuRecTitle: "Impfungen",
    immuRecVaccine: "Impfstoff",
    immuRecDate: "Datum der Impfung",
    immuRecDoseNumber: "Dosis Nummer",
    immuRecLotNumber: "Chargen-Nr. (optional)",
    immuRecLotPlaceholder: "z. B. ABC123",
    immuRecRemove: "Impfung entfernen",
    immuRecAdd: "Neue Impfung hinzufügen",
    immuRecEmpty: "Keine Impfungen dokumentiert",
    immuRecDose: "Dosis {n}",
    immuRecOfSeries: "von {n}",
    immuRecStatusNotDocumented: "Nicht dokumentiert",
    immuRecStatusCurrent: "Aktuell",
    immuRecStatusBooster: "Auffrischung empfohlen",
    immuRecStatusExpired: "Abgelaufen",
    immuRecVacHepA: "Hepatitis A",
    immuRecVacHepB: "Hepatitis B",
    immuRecVacPneumococcal: "Pneumokokken",
    immuRecVacMmr: "MMR (Masern, Mumps, Röteln)",
    immuRecVacTdap: "Tdap (Tetanus, Diphtherie, Keuchhusten)",
    immuRecVacInfluenza: "Influenza",
    immuRecVacCovid: "COVID-19",
    immuRecVacYellowFever: "Gelbfieber",
    immuRecVacMeningococcal: "Meningokokken",
    immuRecVacJapEnc: "Japanische Enzephalitis",
    immuRecVacTyphoid: "Typhus",
    immuRecVacRabies: "Tollwut",
  },
  fr: {
    thdTitle: "Données de santé pour le voyage",
    thdInsurance: "Assurance voyage",
    thdInsuranceProvider: "Assureur",
    thdInsuranceProviderPlaceholder: "p. ex. Allianz Travel, AXA Assistance",
    thdPolicyNumber: "Numéro de police",
    thdValidUntil: "Valable jusqu'au",
    thdEmergencyPhone: "Numéro d'urgence",
    thdEmergencyPhonePlaceholder: "+33 x xx xx xx xx",
    thdCoverage: "Garanties",
    thdCovEmergency: "Soins d'urgence",
    thdCovHospital: "Hospitalisation",
    thdCovMedication: "Médicaments",
    thdCovRepatriation: "Rapatriement sanitaire",
    thdCovDental: "Soins dentaires",
    thdCovEye: "Soins ophtalmologiques",
    thdCovPhysio: "Kinésithérapie",
    thdCovPsychotherapy: "Psychothérapie",
    thdCovChronic: "Maladies chroniques",
    thdCovPregnancy: "Grossesse",
    thdCovAccident: "Assurance accidents",
    thdHospitalsEdit: "Hôpitaux/cliniques préférés",
    thdHospitals: "Hôpitaux préférés",
    thdHospitalName: "Nom de la clinique",
    thdAddress: "Adresse",
    thdPhone: "Numéro de téléphone",
    thdCountry: "Pays",
    thdAddHospital: "Ajouter un hôpital",
    thdDietaryEdit: "Exigences alimentaires particulières",
    thdDietary: "Exigences alimentaires",
    thdDietVegetarian: "Végétarien",
    thdDietVegan: "Végan",
    thdDietHalal: "Halal",
    thdDietKosher: "Casher",
    thdDietGlutenFree: "Sans gluten",
    thdDietLactoseFree: "Sans lactose",
    thdDietDiabetic: "Régime diabétique",
    thdDietOther: "Autre",
    thdSevPreference: "Préférence",
    thdSevMild: "Légère",
    thdSevModerate: "Modérée",
    thdSevSevere: "Sévère",
    thdSevLifeThreatening: "Potentiellement mortelle",
    thdDescriptionOptional: "Description (facultative)",
    thdAddDietary: "Ajouter une exigence alimentaire",
    thdMobility: "Aide à la mobilité",
    thdWheelchairRequired: "Fauteuil roulant nécessaire",
    thdWalkingAid: "Aide à la marche",
    thdAidNone: "Aucune aide à la marche",
    thdAidCane: "Canne",
    thdAidWalker: "Déambulateur",
    thdAidCrutches: "Béquilles",
    thdAssistanceLevel: "Niveau d'assistance",
    thdAssistIndependent: "Autonome",
    thdAssistMinimal: "Assistance minimale",
    thdAssistModerate: "Assistance modérée",
    thdAssistFull: "Assistance complète",
    thdSpecialRequests: "Demandes particulières",
    thdSpecialRequestsPlaceholder: "Besoins ou aides spécifiques",
    thdCommunication: "Besoins de communication",
    thdPrimaryLanguage: "Langue principale",
    thdInterpreterRequired: "Interprète nécessaire",
    thdSignLanguage: "Langue des signes",
    thdHearingImpairment: "Déficience auditive",
    thdVisualImpairment: "Déficience visuelle",
    thdCommunicationAids: "Aides",
    thdExemptions: "Exemptions vaccinales",
    thdVaccine: "Vaccin",
    thdExMedical: "Médicale",
    thdExReligious: "Religieuse",
    thdExPhilosophical: "Philosophique",
    thdAddExemption: "Ajouter une exemption vaccinale",
    thdReason: "Motif",
    thdReasonMedical: "Contre-indication médicale",
    thdReasonReligious: "Motifs religieux",
    thdReasonPhilosophical: "Motifs philosophiques",
    thdReasonOther: "Autres motifs",
    thdProvider: "Assureur",
    thdPolicy: "Police",
    thdCoveredServices: "Prestations couvertes",
    thdEmpty: "Aucune donnée de santé pour le voyage enregistrée",

    famHistTitle: "Antécédents familiaux",
    famHistInfoTitle: "Pourquoi vos antécédents familiaux comptent",
    famHistInfoText:
      "Ces informations aident les médecins à évaluer les risques génétiques et à recommander des mesures de prévention.",
    famHistRelMother: "Mère",
    famHistRelFather: "Père",
    famHistRelSibling: "Frère/sœur",
    famHistRelChild: "Enfant",
    famHistRelGrandparent: "Grands-parents",
    famHistRelAunt: "Tante",
    famHistRelUncle: "Oncle",
    famHistRelCousin: "Cousin/cousine",
    famHistRelOther: "Autre",
    famHistRelationship: "Lien de parenté",
    famHistRelationshipSpecify: "Préciser le lien de parenté",
    famHistRelationshipPlaceholder: "p. ex. beau-père, demi-sœur",
    famHistCondition: "Maladie",
    famHistConditionSelect: "Choisir ou saisir une maladie",
    famHistOtherConditionPlaceholder: "Saisir une autre maladie",
    famHistCatCardiovascular: "Cardiovasculaire",
    famHistCatCancer: "Cancer",
    famHistCatNeurological: "Neurologique",
    famHistCatMetabolic: "Métabolique",
    famHistCatMental: "Santé mentale",
    famHistCatOther: "Autre",
    famHistCondCoronaryArteryDisease: "Maladie coronarienne",
    famHistCondHypertension: "Hypertension artérielle",
    famHistCondHeartFailure: "Insuffisance cardiaque",
    famHistCondBreastCancer: "Cancer du sein",
    famHistCondColorectalCancer: "Cancer colorectal",
    famHistCondPancreaticCancer: "Cancer du pancréas",
    famHistCondProstateCancer: "Cancer de la prostate",
    famHistCondOvarianCancer: "Cancer de l'ovaire",
    famHistCondLungCancer: "Cancer du poumon",
    famHistCondAlzheimers: "Maladie d'Alzheimer",
    famHistCondParkinsons: "Maladie de Parkinson",
    famHistCondMultipleSclerosis: "Sclérose en plaques",
    famHistCondEpilepsy: "Épilepsie",
    famHistCondDiabetesType2: "Diabète de type 2",
    famHistCondDiabetesType1: "Diabète de type 1",
    famHistCondHypothyroidism: "Hypothyroïdie",
    famHistCondHyperthyroidism: "Hyperthyroïdie",
    famHistCondDepression: "Dépression",
    famHistCondSchizophrenia: "Schizophrénie",
    famHistCondBipolar: "Trouble bipolaire",
    famHistCondAnxiety: "Trouble anxieux",
    famHistCondArthritis: "Rhumatisme/arthrite",
    famHistCondCrohns: "Maladie de Crohn",
    famHistCondUlcerativeColitis: "Rectocolite hémorragique",
    famHistCondAsthma: "Asthme",
    famHistStatusActive: "Active/en cours",
    famHistStatusResolved: "Guérie/surmontée",
    famHistStatusDeceased: "Décédé(e) (de cette maladie)",
    famHistGeneticRisk: "Risque génétique (estimé)",
    famHistRiskLow: "Faible",
    famHistRiskModerate: "Modéré",
    famHistRiskHigh: "Élevé",
    famHistRiskLowBadge: "Risque faible",
    famHistRiskModerateBadge: "Risque modéré",
    famHistRiskHighBadge: "Risque élevé",
    famHistAgeAtOnset: "Âge au début de la maladie",
    famHistAgeAtDeath: "Âge au décès",
    famHistYears: "Ans",
    famHistCauseOfDeath: "Cause du décès (si différente)",
    famHistCauseOfDeathShort: "Cause du décès",
    famHistCauseOfDeathPlaceholder: "Si ce n'est pas la maladie ci-dessus",
    famHistNotes: "Notes complémentaires",
    famHistNotesPlaceholder: "Autres détails, complications, etc.",
    famHistRemoveEntry: "Supprimer l'entrée",
    famHistAddEntry: "Ajouter un antécédent familial",
    famHistEmpty: "Aucun antécédent familial enregistré",
    famHistEntryOne: "{count} entrée",
    famHistEntryMany: "{count} entrées",
    famHistOnsetAt: "Diagnostiqué(e) à {age} ans",
    famHistDiedAt: "Décédé(e) à {age} ans",

    immuRecTitle: "Vaccinations",
    immuRecVaccine: "Vaccin",
    immuRecDate: "Date de vaccination",
    immuRecDoseNumber: "Numéro de dose",
    immuRecLotNumber: "Numéro de lot (facultatif)",
    immuRecLotPlaceholder: "p. ex. ABC123",
    immuRecRemove: "Supprimer la vaccination",
    immuRecAdd: "Ajouter une vaccination",
    immuRecEmpty: "Aucune vaccination enregistrée",
    immuRecDose: "Dose {n}",
    immuRecOfSeries: "sur {n}",
    immuRecStatusNotDocumented: "Non documenté",
    immuRecStatusCurrent: "À jour",
    immuRecStatusBooster: "Rappel recommandé",
    immuRecStatusExpired: "Expiré",
    immuRecVacHepA: "Hépatite A",
    immuRecVacHepB: "Hépatite B",
    immuRecVacPneumococcal: "Pneumocoque",
    immuRecVacMmr: "ROR (rougeole, oreillons, rubéole)",
    immuRecVacTdap: "dTca (tétanos, diphtérie, coqueluche)",
    immuRecVacInfluenza: "Grippe",
    immuRecVacCovid: "COVID-19",
    immuRecVacYellowFever: "Fièvre jaune",
    immuRecVacMeningococcal: "Méningocoque",
    immuRecVacJapEnc: "Encéphalite japonaise",
    immuRecVacTyphoid: "Typhoïde",
    immuRecVacRabies: "Rage",
  },
  es: {
    thdTitle: "Datos de salud para viajes",
    thdInsurance: "Seguro de viaje",
    thdInsuranceProvider: "Aseguradora",
    thdInsuranceProviderPlaceholder: "p. ej. Allianz Travel, Mapfre",
    thdPolicyNumber: "Número de póliza",
    thdValidUntil: "Válido hasta",
    thdEmergencyPhone: "Teléfono de emergencia",
    thdEmergencyPhonePlaceholder: "+34 xxx xxx xxx",
    thdCoverage: "Coberturas",
    thdCovEmergency: "Atención de urgencia",
    thdCovHospital: "Hospitalización",
    thdCovMedication: "Medicamentos",
    thdCovRepatriation: "Repatriación sanitaria",
    thdCovDental: "Tratamiento dental",
    thdCovEye: "Atención oftalmológica",
    thdCovPhysio: "Fisioterapia",
    thdCovPsychotherapy: "Psicoterapia",
    thdCovChronic: "Enfermedades crónicas",
    thdCovPregnancy: "Embarazo",
    thdCovAccident: "Seguro de accidentes",
    thdHospitalsEdit: "Hospitales/clínicas preferidos",
    thdHospitals: "Hospitales preferidos",
    thdHospitalName: "Nombre de la clínica",
    thdAddress: "Dirección",
    thdPhone: "Número de teléfono",
    thdCountry: "País",
    thdAddHospital: "Añadir hospital",
    thdDietaryEdit: "Requisitos alimentarios especiales",
    thdDietary: "Requisitos alimentarios",
    thdDietVegetarian: "Vegetariana",
    thdDietVegan: "Vegana",
    thdDietHalal: "Halal",
    thdDietKosher: "Kosher",
    thdDietGlutenFree: "Sin gluten",
    thdDietLactoseFree: "Sin lactosa",
    thdDietDiabetic: "Dieta para diabéticos",
    thdDietOther: "Otra",
    thdSevPreference: "Preferencia",
    thdSevMild: "Leve",
    thdSevModerate: "Moderada",
    thdSevSevere: "Grave",
    thdSevLifeThreatening: "Potencialmente mortal",
    thdDescriptionOptional: "Descripción (opcional)",
    thdAddDietary: "Añadir requisito alimentario",
    thdMobility: "Ayuda a la movilidad",
    thdWheelchairRequired: "Necesita silla de ruedas",
    thdWalkingAid: "Ayuda para caminar",
    thdAidNone: "Sin ayuda para caminar",
    thdAidCane: "Bastón",
    thdAidWalker: "Andador",
    thdAidCrutches: "Muletas",
    thdAssistanceLevel: "Nivel de asistencia",
    thdAssistIndependent: "Independiente",
    thdAssistMinimal: "Asistencia mínima",
    thdAssistModerate: "Asistencia moderada",
    thdAssistFull: "Asistencia completa",
    thdSpecialRequests: "Peticiones especiales",
    thdSpecialRequestsPlaceholder: "Necesidades o ayudas especiales",
    thdCommunication: "Necesidades de comunicación",
    thdPrimaryLanguage: "Idioma principal",
    thdInterpreterRequired: "Necesita intérprete",
    thdSignLanguage: "Lengua de signos",
    thdHearingImpairment: "Discapacidad auditiva",
    thdVisualImpairment: "Discapacidad visual",
    thdCommunicationAids: "Ayudas",
    thdExemptions: "Exenciones de vacunación",
    thdVaccine: "Vacuna",
    thdExMedical: "Médica",
    thdExReligious: "Religiosa",
    thdExPhilosophical: "Filosófica",
    thdAddExemption: "Añadir exención de vacunación",
    thdReason: "Motivo",
    thdReasonMedical: "Contraindicación médica",
    thdReasonReligious: "Motivos religiosos",
    thdReasonPhilosophical: "Motivos filosóficos",
    thdReasonOther: "Otros motivos",
    thdProvider: "Aseguradora",
    thdPolicy: "Póliza",
    thdCoveredServices: "Prestaciones cubiertas",
    thdEmpty: "No hay datos de salud para viajes registrados",

    famHistTitle: "Antecedentes familiares",
    famHistInfoTitle: "Por qué importan sus antecedentes familiares",
    famHistInfoText:
      "Esta información ayuda a los médicos a evaluar riesgos genéticos y recomendar medidas preventivas.",
    famHistRelMother: "Madre",
    famHistRelFather: "Padre",
    famHistRelSibling: "Hermano/a",
    famHistRelChild: "Hijo/a",
    famHistRelGrandparent: "Abuelos",
    famHistRelAunt: "Tía",
    famHistRelUncle: "Tío",
    famHistRelCousin: "Primo/a",
    famHistRelOther: "Otro",
    famHistRelationship: "Parentesco",
    famHistRelationshipSpecify: "Especificar parentesco",
    famHistRelationshipPlaceholder: "p. ej. padrastro, media hermana",
    famHistCondition: "Enfermedad",
    famHistConditionSelect: "Seleccione o introduzca una enfermedad",
    famHistOtherConditionPlaceholder: "Introduzca otra enfermedad",
    famHistCatCardiovascular: "Cardiovascular",
    famHistCatCancer: "Cáncer",
    famHistCatNeurological: "Neurológica",
    famHistCatMetabolic: "Metabólica",
    famHistCatMental: "Salud mental",
    famHistCatOther: "Otras",
    famHistCondCoronaryArteryDisease: "Enfermedad coronaria",
    famHistCondHypertension: "Hipertensión arterial",
    famHistCondHeartFailure: "Insuficiencia cardíaca",
    famHistCondBreastCancer: "Cáncer de mama",
    famHistCondColorectalCancer: "Cáncer colorrectal",
    famHistCondPancreaticCancer: "Cáncer de páncreas",
    famHistCondProstateCancer: "Cáncer de próstata",
    famHistCondOvarianCancer: "Cáncer de ovario",
    famHistCondLungCancer: "Cáncer de pulmón",
    famHistCondAlzheimers: "Enfermedad de Alzheimer",
    famHistCondParkinsons: "Enfermedad de Parkinson",
    famHistCondMultipleSclerosis: "Esclerosis múltiple",
    famHistCondEpilepsy: "Epilepsia",
    famHistCondDiabetesType2: "Diabetes tipo 2",
    famHistCondDiabetesType1: "Diabetes tipo 1",
    famHistCondHypothyroidism: "Hipotiroidismo",
    famHistCondHyperthyroidism: "Hipertiroidismo",
    famHistCondDepression: "Depresión",
    famHistCondSchizophrenia: "Esquizofrenia",
    famHistCondBipolar: "Trastorno bipolar",
    famHistCondAnxiety: "Trastorno de ansiedad",
    famHistCondArthritis: "Reumatismo/artritis",
    famHistCondCrohns: "Enfermedad de Crohn",
    famHistCondUlcerativeColitis: "Colitis ulcerosa",
    famHistCondAsthma: "Asma",
    famHistStatusActive: "Activa/en curso",
    famHistStatusResolved: "Curada/superada",
    famHistStatusDeceased: "Fallecido/a (por esta enfermedad)",
    famHistGeneticRisk: "Riesgo genético (estimado)",
    famHistRiskLow: "Bajo",
    famHistRiskModerate: "Moderado",
    famHistRiskHigh: "Alto",
    famHistRiskLowBadge: "Riesgo bajo",
    famHistRiskModerateBadge: "Riesgo moderado",
    famHistRiskHighBadge: "Riesgo alto",
    famHistAgeAtOnset: "Edad al inicio",
    famHistAgeAtDeath: "Edad al fallecer",
    famHistYears: "Años",
    famHistCauseOfDeath: "Causa de la muerte (si es distinta)",
    famHistCauseOfDeathShort: "Causa de la muerte",
    famHistCauseOfDeathPlaceholder: "Si no es la enfermedad indicada arriba",
    famHistNotes: "Notas adicionales",
    famHistNotesPlaceholder: "Más detalles, complicaciones, etc.",
    famHistRemoveEntry: "Eliminar entrada",
    famHistAddEntry: "Añadir antecedente familiar",
    famHistEmpty: "No hay antecedentes familiares registrados",
    famHistEntryOne: "{count} entrada",
    famHistEntryMany: "{count} entradas",
    famHistOnsetAt: "Diagnosticado/a a los {age} años",
    famHistDiedAt: "Fallecido/a a los {age} años",

    immuRecTitle: "Vacunas",
    immuRecVaccine: "Vacuna",
    immuRecDate: "Fecha de vacunación",
    immuRecDoseNumber: "Número de dosis",
    immuRecLotNumber: "Número de lote (opcional)",
    immuRecLotPlaceholder: "p. ej. ABC123",
    immuRecRemove: "Eliminar vacuna",
    immuRecAdd: "Añadir vacuna",
    immuRecEmpty: "No hay vacunas registradas",
    immuRecDose: "Dosis {n}",
    immuRecOfSeries: "de {n}",
    immuRecStatusNotDocumented: "No documentada",
    immuRecStatusCurrent: "Vigente",
    immuRecStatusBooster: "Refuerzo recomendado",
    immuRecStatusExpired: "Caducada",
    immuRecVacHepA: "Hepatitis A",
    immuRecVacHepB: "Hepatitis B",
    immuRecVacPneumococcal: "Neumococo",
    immuRecVacMmr: "Triple vírica (sarampión, paperas, rubéola)",
    immuRecVacTdap: "Tdpa (tétanos, difteria, tos ferina)",
    immuRecVacInfluenza: "Gripe",
    immuRecVacCovid: "COVID-19",
    immuRecVacYellowFever: "Fiebre amarilla",
    immuRecVacMeningococcal: "Meningococo",
    immuRecVacJapEnc: "Encefalitis japonesa",
    immuRecVacTyphoid: "Fiebre tifoidea",
    immuRecVacRabies: "Rabia",
  },
  pt: {
    thdTitle: "Dados de saúde para viagem",
    thdInsurance: "Seguro de viagem",
    thdInsuranceProvider: "Seguradora",
    thdInsuranceProviderPlaceholder: "p. ex. Allianz Travel, Fidelidade",
    thdPolicyNumber: "Número da apólice",
    thdValidUntil: "Válido até",
    thdEmergencyPhone: "Telefone de emergência",
    thdEmergencyPhonePlaceholder: "+351 xxx xxx xxx",
    thdCoverage: "Coberturas",
    thdCovEmergency: "Tratamento de urgência",
    thdCovHospital: "Internamento hospitalar",
    thdCovMedication: "Medicamentos",
    thdCovRepatriation: "Repatriamento sanitário",
    thdCovDental: "Tratamento dentário",
    thdCovEye: "Cuidados oftalmológicos",
    thdCovPhysio: "Fisioterapia",
    thdCovPsychotherapy: "Psicoterapia",
    thdCovChronic: "Doenças crónicas",
    thdCovPregnancy: "Gravidez",
    thdCovAccident: "Seguro de acidentes",
    thdHospitalsEdit: "Hospitais/clínicas preferidos",
    thdHospitals: "Hospitais preferidos",
    thdHospitalName: "Nome da clínica",
    thdAddress: "Morada",
    thdPhone: "Número de telefone",
    thdCountry: "País",
    thdAddHospital: "Adicionar hospital",
    thdDietaryEdit: "Requisitos alimentares especiais",
    thdDietary: "Requisitos alimentares",
    thdDietVegetarian: "Vegetariana",
    thdDietVegan: "Vegana",
    thdDietHalal: "Halal",
    thdDietKosher: "Kosher",
    thdDietGlutenFree: "Sem glúten",
    thdDietLactoseFree: "Sem lactose",
    thdDietDiabetic: "Dieta para diabéticos",
    thdDietOther: "Outra",
    thdSevPreference: "Preferência",
    thdSevMild: "Ligeira",
    thdSevModerate: "Moderada",
    thdSevSevere: "Grave",
    thdSevLifeThreatening: "Potencialmente fatal",
    thdDescriptionOptional: "Descrição (opcional)",
    thdAddDietary: "Adicionar requisito alimentar",
    thdMobility: "Apoio à mobilidade",
    thdWheelchairRequired: "Necessita de cadeira de rodas",
    thdWalkingAid: "Auxiliar de marcha",
    thdAidNone: "Sem auxiliar de marcha",
    thdAidCane: "Bengala",
    thdAidWalker: "Andarilho",
    thdAidCrutches: "Canadianas",
    thdAssistanceLevel: "Nível de assistência",
    thdAssistIndependent: "Autónomo",
    thdAssistMinimal: "Assistência mínima",
    thdAssistModerate: "Assistência moderada",
    thdAssistFull: "Assistência total",
    thdSpecialRequests: "Pedidos especiais",
    thdSpecialRequestsPlaceholder: "Necessidades ou auxiliares específicos",
    thdCommunication: "Necessidades de comunicação",
    thdPrimaryLanguage: "Língua principal",
    thdInterpreterRequired: "Necessita de intérprete",
    thdSignLanguage: "Língua gestual",
    thdHearingImpairment: "Deficiência auditiva",
    thdVisualImpairment: "Deficiência visual",
    thdCommunicationAids: "Auxiliares",
    thdExemptions: "Isenções de vacinação",
    thdVaccine: "Vacina",
    thdExMedical: "Médica",
    thdExReligious: "Religiosa",
    thdExPhilosophical: "Filosófica",
    thdAddExemption: "Adicionar isenção de vacinação",
    thdReason: "Motivo",
    thdReasonMedical: "Contraindicação médica",
    thdReasonReligious: "Motivos religiosos",
    thdReasonPhilosophical: "Motivos filosóficos",
    thdReasonOther: "Outros motivos",
    thdProvider: "Seguradora",
    thdPolicy: "Apólice",
    thdCoveredServices: "Prestações cobertas",
    thdEmpty: "Sem dados de saúde para viagem registados",

    famHistTitle: "Antecedentes familiares",
    famHistInfoTitle: "Porque os seus antecedentes familiares são importantes",
    famHistInfoText:
      "Estas informações ajudam os médicos a avaliar riscos genéticos e a recomendar medidas preventivas.",
    famHistRelMother: "Mãe",
    famHistRelFather: "Pai",
    famHistRelSibling: "Irmão/irmã",
    famHistRelChild: "Filho/filha",
    famHistRelGrandparent: "Avós",
    famHistRelAunt: "Tia",
    famHistRelUncle: "Tio",
    famHistRelCousin: "Primo/prima",
    famHistRelOther: "Outro",
    famHistRelationship: "Grau de parentesco",
    famHistRelationshipSpecify: "Especificar grau de parentesco",
    famHistRelationshipPlaceholder: "p. ex. padrasto, meia-irmã",
    famHistCondition: "Doença",
    famHistConditionSelect: "Selecione ou introduza uma doença",
    famHistOtherConditionPlaceholder: "Introduza outra doença",
    famHistCatCardiovascular: "Cardiovascular",
    famHistCatCancer: "Cancro",
    famHistCatNeurological: "Neurológica",
    famHistCatMetabolic: "Metabólica",
    famHistCatMental: "Saúde mental",
    famHistCatOther: "Outras",
    famHistCondCoronaryArteryDisease: "Doença coronária",
    famHistCondHypertension: "Hipertensão arterial",
    famHistCondHeartFailure: "Insuficiência cardíaca",
    famHistCondBreastCancer: "Cancro da mama",
    famHistCondColorectalCancer: "Cancro colorretal",
    famHistCondPancreaticCancer: "Cancro do pâncreas",
    famHistCondProstateCancer: "Cancro da próstata",
    famHistCondOvarianCancer: "Cancro do ovário",
    famHistCondLungCancer: "Cancro do pulmão",
    famHistCondAlzheimers: "Doença de Alzheimer",
    famHistCondParkinsons: "Doença de Parkinson",
    famHistCondMultipleSclerosis: "Esclerose múltipla",
    famHistCondEpilepsy: "Epilepsia",
    famHistCondDiabetesType2: "Diabetes tipo 2",
    famHistCondDiabetesType1: "Diabetes tipo 1",
    famHistCondHypothyroidism: "Hipotiroidismo",
    famHistCondHyperthyroidism: "Hipertiroidismo",
    famHistCondDepression: "Depressão",
    famHistCondSchizophrenia: "Esquizofrenia",
    famHistCondBipolar: "Perturbação bipolar",
    famHistCondAnxiety: "Perturbação de ansiedade",
    famHistCondArthritis: "Reumatismo/artrite",
    famHistCondCrohns: "Doença de Crohn",
    famHistCondUlcerativeColitis: "Colite ulcerosa",
    famHistCondAsthma: "Asma",
    famHistStatusActive: "Ativa/em curso",
    famHistStatusResolved: "Curada/ultrapassada",
    famHistStatusDeceased: "Falecido/a (devido a esta doença)",
    famHistGeneticRisk: "Risco genético (estimado)",
    famHistRiskLow: "Baixo",
    famHistRiskModerate: "Moderado",
    famHistRiskHigh: "Alto",
    famHistRiskLowBadge: "Risco baixo",
    famHistRiskModerateBadge: "Risco moderado",
    famHistRiskHighBadge: "Risco alto",
    famHistAgeAtOnset: "Idade no início da doença",
    famHistAgeAtDeath: "Idade à data da morte",
    famHistYears: "Anos",
    famHistCauseOfDeath: "Causa de morte (se diferente)",
    famHistCauseOfDeathShort: "Causa de morte",
    famHistCauseOfDeathPlaceholder: "Se não for a doença indicada acima",
    famHistNotes: "Notas adicionais",
    famHistNotesPlaceholder: "Mais detalhes, complicações, etc.",
    famHistRemoveEntry: "Remover entrada",
    famHistAddEntry: "Adicionar antecedente familiar",
    famHistEmpty: "Sem antecedentes familiares registados",
    famHistEntryOne: "{count} entrada",
    famHistEntryMany: "{count} entradas",
    famHistOnsetAt: "Diagnosticado/a aos {age} anos",
    famHistDiedAt: "Faleceu aos {age} anos",

    immuRecTitle: "Vacinas",
    immuRecVaccine: "Vacina",
    immuRecDate: "Data da vacinação",
    immuRecDoseNumber: "Número da dose",
    immuRecLotNumber: "Número do lote (opcional)",
    immuRecLotPlaceholder: "p. ex. ABC123",
    immuRecRemove: "Remover vacina",
    immuRecAdd: "Adicionar vacina",
    immuRecEmpty: "Sem vacinas registadas",
    immuRecDose: "Dose {n}",
    immuRecOfSeries: "de {n}",
    immuRecStatusNotDocumented: "Não documentada",
    immuRecStatusCurrent: "Em dia",
    immuRecStatusBooster: "Reforço recomendado",
    immuRecStatusExpired: "Expirada",
    immuRecVacHepA: "Hepatite A",
    immuRecVacHepB: "Hepatite B",
    immuRecVacPneumococcal: "Pneumocócica",
    immuRecVacMmr: "VASPR (sarampo, papeira, rubéola)",
    immuRecVacTdap: "Tdpa (tétano, difteria, tosse convulsa)",
    immuRecVacInfluenza: "Gripe",
    immuRecVacCovid: "COVID-19",
    immuRecVacYellowFever: "Febre amarela",
    immuRecVacMeningococcal: "Meningocócica",
    immuRecVacJapEnc: "Encefalite japonesa",
    immuRecVacTyphoid: "Febre tifoide",
    immuRecVacRabies: "Raiva",
  },
};
